import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useClinica } from '@/contexts/ClinicaContext';

/**
 * Hook para gerenciar configuração de distribuição automática de leads
 * 
 * O que faz:
 * - Busca configuração atual da clínica (etapa destino, ativação e filtro de anúncios)
 * - Permite ativar/desativar distribuição automática
 * - Permite definir qual etapa receberá novos leads automaticamente
 * - Permite restringir a distribuição automática a leads de anúncios
 * 
 * Onde é usado:
 * - LeadDistributionSettings.tsx (componente de configuração)
 * - Hooks de criação de leads (para aplicar distribuição automática)
 * 
 * Como se conecta:
 * - Tabela clinicas: campos auto_lead_distribution_enabled, default_etapa_id e auto_lead_distribution_ads_only
 * - useClinica: para obter ID da clínica atual
 */

interface LeadDistributionConfig {
  auto_lead_distribution_enabled: boolean;
  default_etapa_id: string | null;
  auto_lead_distribution_ads_only: boolean;
}

export const useLeadDistributionConfig = () => {
  const { clinicaAtiva } = useClinica();
  const queryClient = useQueryClient();

  // Buscar configuração atual
  const { data: config, isLoading } = useQuery({
    queryKey: ['lead-distribution-config', clinicaAtiva?.id],
    queryFn: async (): Promise<LeadDistributionConfig | null> => {
      if (!clinicaAtiva?.id) return null;

      // Tabela: clinicas | Operação: SELECT | Campos: ativação, etapa padrão e filtro de anúncios.
      // Filtro: id = clínica ativa | Risco: apenas lê preferências de distribuição da clínica atual.
      const { data, error } = await supabase
        .from('clinicas')
        .select('auto_lead_distribution_enabled, default_etapa_id, auto_lead_distribution_ads_only')
        .eq('id', clinicaAtiva.id)
        .single();

      if (error) {
        console.error('❌ Erro ao buscar configuração de distribuição:', error);
        throw error;
      }

      return data as LeadDistributionConfig;
    },
    enabled: !!clinicaAtiva?.id,
  });

  // Salva se a distribuição deve alcançar todos os leads ou somente os reconhecidos como anúncios.
  const setDistributionAdsOnlyMutation = useMutation({
    mutationFn: async (adsOnly: boolean) => {
      if (!clinicaAtiva?.id) throw new Error('Clínica não encontrada');

      // Tabela: clinicas | Operação: UPDATE | Campo: auto_lead_distribution_ads_only.
      // Filtro: id = clínica ativa | Risco: altera apenas a preferência de roteamento dessa clínica.
      const { error } = await supabase
        .from('clinicas')
        .update({ auto_lead_distribution_ads_only: adsOnly })
        .eq('id', clinicaAtiva.id);

      if (error) throw error;
    },
    onSuccess: (_, adsOnly) => {
      queryClient.invalidateQueries({ queryKey: ['lead-distribution-config'] });
      queryClient.invalidateQueries({ queryKey: ['clinicaData'] });
      toast.success(
        adsOnly
          ? 'Distribuição limitada a leads de anúncios.'
          : 'Distribuição aplicada a todos os novos leads.',
      );
    },
    onError: (error) => {
      console.error('❌ Erro ao alterar o filtro de anúncios:', error);
      toast.error('Erro ao atualizar o tipo de lead distribuído');
    },
  });

  // Atualizar estado de ativação
  const toggleDistribution = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!clinicaAtiva?.id) throw new Error('Clínica não encontrada');

      const { error } = await supabase
        .from('clinicas')
        .update({ auto_lead_distribution_enabled: enabled })
        .eq('id', clinicaAtiva.id);

      if (error) throw error;
    },
    onSuccess: (_, enabled) => {
      queryClient.invalidateQueries({ queryKey: ['lead-distribution-config'] });
      queryClient.invalidateQueries({ queryKey: ['clinicaData'] });
      toast.success(
        enabled 
          ? 'Distribuição automática ativada!' 
          : 'Distribuição automática desativada!'
      );
    },
    onError: (error) => {
      console.error('❌ Erro ao alterar estado:', error);
      toast.error('Erro ao atualizar configuração');
    },
  });

  // Definir etapa destino padrão
  const setDefaultEtapa = useMutation({
    mutationFn: async (etapaId: string) => {
      if (!clinicaAtiva?.id) throw new Error('Clínica não encontrada');

      const { error } = await supabase
        .from('clinicas')
        .update({ default_etapa_id: etapaId })
        .eq('id', clinicaAtiva.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-distribution-config'] });
      queryClient.invalidateQueries({ queryKey: ['clinicaData'] });
      toast.success('Etapa destino atualizada!');
    },
    onError: (error) => {
      console.error('❌ Erro ao definir etapa:', error);
      toast.error('Erro ao atualizar etapa destino');
    },
  });

  return {
    config,
    isLoading,
    isEnabled: config?.auto_lead_distribution_enabled ?? false,
    defaultEtapaId: config?.default_etapa_id,
    adsOnly: config?.auto_lead_distribution_ads_only ?? false,
    toggleDistribution: toggleDistribution.mutate,
    setDistributionAdsOnly: setDistributionAdsOnlyMutation.mutate,
    setDefaultEtapa: setDefaultEtapa.mutate,
    isUpdating:
      toggleDistribution.isPending
      || setDistributionAdsOnlyMutation.isPending
      || setDefaultEtapa.isPending,
  };
};
